import React, { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { logError } from "@/lib/logger";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  LifeBuoy,
  Loader2,
  RefreshCw,
  Star,
  Archive,
  ArchiveRestore,
  Trash2,
  Reply,
  Forward,
  MailOpen,
  Mail,
  Send,
  KeyRound,
} from "lucide-react";
import { toast } from "sonner";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "unread", label: "Unread" },
  { key: "starred", label: "Starred" },
  { key: "archived", label: "Archived" },
];

const CATEGORY_LABEL = {
  billing: "Billing",
  technical: "Technical",
  feature: "Feature Request",
  password_reset: "Password Reset",
  other: "Other",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch (e) {
    return iso;
  }
}

// Client-side filter (server returns all tickets + counts).
function applyFilter(tickets, filter) {
  if (filter === "unread") return tickets.filter((t) => t.status === "unread" && !t.archived);
  if (filter === "starred") return tickets.filter((t) => t.starred && !t.archived);
  if (filter === "archived") return tickets.filter((t) => t.archived);
  return tickets.filter((t) => !t.archived); // "all" = active inbox (non-archived)
}

function KindBadge({ kind }) {
  const map = {
    original: { label: "Message", cls: "" },
    user_followup: { label: "User follow-up", cls: "" },
    reply: { label: "Support reply", cls: "bg-primary hover:bg-primary" },
    forward: { label: "Forwarded", cls: "bg-amber-500 hover:bg-amber-500" },
  };
  const m = map[kind] || { label: kind, cls: "" };
  return <Badge className={m.cls} variant={m.cls ? undefined : "outline"}>{m.label}</Badge>;
}

// Detail modal with full thread + all admin actions.
function TicketDetail({ ticket, onClose, onChanged, onDeleted }) {
  const [reply, setReply] = useState("");
  const [fwdTo, setFwdTo] = useState("");
  const [fwdBody, setFwdBody] = useState("");
  const [showForward, setShowForward] = useState(false);
  const [busy, setBusy] = useState(false);
  const [tempPw, setTempPw] = useState("");
  const [resetting, setResetting] = useState(false);

  if (!ticket) return null;

  const isPasswordReset = ticket.category === "password_reset";

  // Support-assisted password reset: reset the requesting user's password by the
  // ticket's email (non-admin only), then auto-insert a reply with the temp
  // password so the admin can send it to the user in-app.
  const resetPassword = async () => {
    const pw = (tempPw || "").trim();
    if (pw.length < 6) {
      toast.error("Temporary password must be at least 6 characters.");
      return;
    }
    setResetting(true);
    try {
      await api.adminResetPasswordByEmail(ticket.email, pw);
      toast.success(`Password reset for ${ticket.email}.`);
      setReply(
        `Your SalesTap password has been reset. Your temporary password is: ${pw}\n\n` +
          "Please log in with it and change your password right away."
      );
      setTempPw("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not reset password.");
    } finally {
      setResetting(false);
    }
  };

  const act = async (fn, okMsg) => {
    setBusy(true);
    try {
      const res = await fn();
      if (okMsg) toast.success(okMsg);
      if (res?.ticket) onChanged(res.ticket);
      return res;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed.");
      return null;
    } finally {
      setBusy(false);
    }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    const res = await act(() => api.adminSupportReply(ticket.id, reply.trim()), "Reply sent to user.");
    if (res) setReply("");
  };

  const sendForward = async () => {
    if (!fwdTo.trim()) {
      toast.error("Enter a forward-to address.");
      return;
    }
    const res = await act(() => api.adminSupportForward(ticket.id, fwdTo.trim(), fwdBody.trim()), "Forwarded (internal note).");
    if (res) {
      setFwdTo("");
      setFwdBody("");
      setShowForward(false);
    }
  };

  const del = async () => {
    if (!window.confirm("Delete this support ticket? This cannot be undone.")) return;
    setBusy(true);
    try {
      await api.adminSupportDelete(ticket.id);
      toast.success("Ticket deleted.");
      onDeleted(ticket.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!ticket} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="admin-support-detail">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 pr-6">
            <LifeBuoy className="w-5 h-5 text-primary flex-shrink-0" />
            <span className="truncate">{ticket.subject}</span>
          </DialogTitle>
          <DialogDescription>
            Manage this support request — reply to the user, forward internally, star, archive or delete.
          </DialogDescription>
        </DialogHeader>

        {/* Meta */}
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <Badge variant="outline">{CATEGORY_LABEL[ticket.category] || "Other"}</Badge>
          <Badge variant={ticket.status === "unread" ? undefined : "secondary"} className={ticket.status === "unread" ? "bg-primary hover:bg-primary" : ""}>
            {ticket.status === "unread" ? "Unread" : "Read"}
          </Badge>
          {ticket.starred && <Badge className="bg-amber-500 hover:bg-amber-500">Starred</Badge>}
          {ticket.archived && <Badge variant="secondary">Archived</Badge>}
          <span className="text-muted-foreground">
            {ticket.name} · {ticket.email} · {ticket.source === "public" ? "Public" : "Account"} · {fmtDate(ticket.created_at)}
          </span>
        </div>

        {/* Action toolbar */}
        <div className="flex flex-wrap items-center gap-1.5 border-y border-border py-2">
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            data-testid="admin-support-toggle-read"
            onClick={() => act(() => api.adminSupportSetRead(ticket.id, ticket.status !== "unread" ? false : true), null)}
            title={ticket.status === "unread" ? "Mark as read" : "Mark as unread"}
          >
            {ticket.status === "unread" ? <MailOpen className="w-3.5 h-3.5" /> : <Mail className="w-3.5 h-3.5" />}
            <span className="ml-1.5">{ticket.status === "unread" ? "Mark read" : "Mark unread"}</span>
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            data-testid="admin-support-toggle-star"
            onClick={() => act(() => api.adminSupportSetStar(ticket.id, !ticket.starred), null)}
            className={ticket.starred ? "text-amber-500" : ""}
            title={ticket.starred ? "Unsave" : "Save (star)"}
          >
            <Star className={`w-3.5 h-3.5 ${ticket.starred ? "fill-amber-500" : ""}`} />
            <span className="ml-1.5">{ticket.starred ? "Saved" : "Save"}</span>
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            data-testid="admin-support-toggle-archive"
            onClick={() => act(() => api.adminSupportSetArchive(ticket.id, !ticket.archived), ticket.archived ? "Unarchived." : "Archived.")}
            title={ticket.archived ? "Unarchive" : "Archive"}
          >
            {ticket.archived ? <ArchiveRestore className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
            <span className="ml-1.5">{ticket.archived ? "Unarchive" : "Archive"}</span>
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            data-testid="admin-support-forward-toggle"
            onClick={() => setShowForward((s) => !s)}
            title="Forward (internal note)"
          >
            <Forward className="w-3.5 h-3.5" />
            <span className="ml-1.5">Forward</span>
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            data-testid="admin-support-delete"
            onClick={del}
            className="text-red-600 hover:text-red-600"
            title="Delete"
          >
            <Trash2 className="w-3.5 h-3.5" />
            <span className="ml-1.5">Delete</span>
          </Button>
        </div>

        {/* Thread */}
        <div className="space-y-2.5 max-h-[300px] overflow-y-auto pr-1" data-testid="admin-support-thread">
          {(ticket.thread || []).map((e) => (
            <div
              key={e.id}
              className={`text-sm rounded-md px-3 py-2 border ${
                e.author === "admin" ? "bg-primary/5 border-primary/20" : "bg-muted/50 border-border"
              }`}
            >
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="flex items-center gap-2">
                  <span className="text-xs font-medium text-foreground">{e.author_name}</span>
                  <KindBadge kind={e.kind} />
                  {e.kind === "forward" && e.forward_to && (
                    <span className="text-[11px] text-muted-foreground">→ {e.forward_to}</span>
                  )}
                </span>
                <span className="text-[10px] text-muted-foreground">{fmtDate(e.created_at)}</span>
              </div>
              {e.body && <p className="text-foreground/90 whitespace-pre-wrap break-words">{e.body}</p>}
            </div>
          ))}
        </div>

        {/* Forward panel */}
        {showForward && (
          <div className="space-y-2 rounded-md border border-amber-500/30 bg-amber-500/5 p-3" data-testid="admin-support-forward-panel">
            <p className="text-xs text-muted-foreground">
              Forward is an internal note (in-app only — no email is sent).
            </p>
            <Input
              data-testid="admin-support-forward-to"
              value={fwdTo}
              onChange={(e) => setFwdTo(e.target.value)}
              placeholder="Forward to (e.g. engineering@salestap.app)"
            />
            <Textarea
              data-testid="admin-support-forward-body"
              value={fwdBody}
              onChange={(e) => setFwdBody(e.target.value)}
              placeholder="Optional note…"
              rows={2}
            />
            <div className="flex justify-end">
              <Button size="sm" disabled={busy} onClick={sendForward} data-testid="admin-support-forward-send">
                <Forward className="w-4 h-4 mr-1.5" /> Forward
              </Button>
            </div>
          </div>
        )}

        {/* Password-reset control (Support-assisted forgot-password flow) */}
        {isPasswordReset && (
          <div className="space-y-2 rounded-md border border-primary/30 bg-primary/5 p-3" data-testid="admin-support-reset-panel">
            <div className="flex items-center gap-1.5 text-sm font-medium text-foreground">
              <KeyRound className="w-4 h-4 text-primary" /> Reset password for {ticket.email}
            </div>
            <p className="text-xs text-muted-foreground">
              Sets a temporary password for the account matching this ticket's email (non-admin
              accounts only). It also pre-fills a reply below so you can send it to the user.
            </p>
            <div className="flex items-end gap-2">
              <Input
                data-testid="admin-support-reset-pw"
                value={tempPw}
                onChange={(e) => setTempPw(e.target.value)}
                placeholder="Temporary password (min 6 chars)"
                className="flex-1"
              />
              <Button
                data-testid="admin-support-reset-btn"
                onClick={resetPassword}
                disabled={resetting || tempPw.trim().length < 6}
              >
                {resetting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <KeyRound className="w-4 h-4 mr-1.5" />}
                Reset password
              </Button>
            </div>
          </div>
        )}

        {/* Reply to user */}
        <div className="space-y-2 pt-1 border-t border-border">
          <div className="flex items-center gap-1.5 text-sm font-medium text-foreground">
            <Reply className="w-4 h-4 text-primary" /> Reply to {ticket.name || "user"}
          </div>
          <Textarea
            data-testid="admin-support-reply-body"
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Type your reply — the user will see this in their requests…"
            rows={3}
          />
          <div className="flex justify-end">
            <Button disabled={busy || !reply.trim()} onClick={sendReply} data-testid="admin-support-reply-send">
              {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
              Send reply
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminSupport() {
  const [tickets, setTickets] = useState([]);
  const [counts, setCounts] = useState({ all: 0, unread: 0, starred: 0, archived: 0 });
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.adminSupportTickets();
      setTickets(res.tickets || []);
      setCounts(res.counts || { all: 0, unread: 0, starred: 0, archived: 0 });
    } catch (e) {
      logError("Failed to load support tickets:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Keep the open detail modal in sync after an action, and refresh counts.
  const onChanged = useCallback((updated) => {
    setTickets((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
    setSelected((cur) => (cur && cur.id === updated.id ? updated : cur));
    load(); // refresh counts (cheap; small dataset)
  }, [load]);

  const onDeleted = useCallback((id) => {
    setTickets((prev) => prev.filter((t) => t.id !== id));
    setSelected(null);
    load();
  }, [load]);

  const openTicket = async (t) => {
    setSelected(t);
    // Opening an unread ticket marks it read (like an inbox).
    if (t.status === "unread") {
      try {
        const res = await api.adminSupportSetRead(t.id, true);
        if (res?.ticket) {
          setSelected(res.ticket);
          setTickets((prev) => prev.map((x) => (x.id === t.id ? res.ticket : x)));
          load();
        }
      } catch (e) {
        logError("Failed to mark ticket read:", e);
      }
    }
  };

  const visible = applyFilter(tickets, filter);

  return (
    <Card data-testid="admin-support">
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle className="text-base flex items-center gap-2">
            <LifeBuoy className="w-4 h-4 text-primary" /> Support Requests
            {counts.unread > 0 && (
              <Badge className="bg-primary hover:bg-primary" data-testid="admin-support-unread-badge">
                {counts.unread} unread
              </Badge>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-md border border-border overflow-hidden" data-testid="admin-support-filters">
              {FILTERS.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  data-testid={`admin-support-filter-${f.key}`}
                  onClick={() => setFilter(f.key)}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                    filter === f.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"
                  }`}
                >
                  {f.label}
                  <span className="ml-1 opacity-70">({counts[f.key] ?? 0})</span>
                </button>
              ))}
            </div>
            <Button data-testid="admin-support-refresh" onClick={load} variant="outline" size="sm">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : visible.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-10" data-testid="admin-support-empty">
            No {filter === "all" ? "" : filter} support requests.
          </p>
        ) : (
          <div className="space-y-1.5" data-testid="admin-support-list">
            {visible.map((t) => (
              <button
                key={t.id}
                type="button"
                data-testid="admin-support-row"
                onClick={() => openTicket(t)}
                className="w-full flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2.5 text-left hover:border-primary/40 hover:bg-muted/40 transition-colors"
              >
                <span className="flex items-center gap-2 min-w-0">
                  {t.status === "unread" ? (
                    <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0" title="Unread" />
                  ) : (
                    <span className="w-2 h-2 rounded-full bg-transparent flex-shrink-0" />
                  )}
                  {t.starred && <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500 flex-shrink-0" />}
                  <span className="min-w-0">
                    <span className={`block truncate ${t.status === "unread" ? "font-semibold text-foreground" : "text-foreground/90"}`}>
                      {t.subject}
                    </span>
                    <span className="block text-xs text-muted-foreground truncate">
                      {t.name} · {t.email}
                    </span>
                  </span>
                </span>
                <span className="flex items-center gap-2 flex-shrink-0">
                  <Badge variant="outline" className="text-[10px]">{CATEGORY_LABEL[t.category] || "Other"}</Badge>
                  <span className="text-[11px] text-muted-foreground hidden sm:inline">{fmtDate(t.created_at)}</span>
                </span>
              </button>
            ))}
          </div>
        )}
      </CardContent>

      <TicketDetail
        ticket={selected}
        onClose={() => setSelected(null)}
        onChanged={onChanged}
        onDeleted={onDeleted}
      />
    </Card>
  );
}
